// =============================================================================
// Aquaestil2 — main JS entry (Joomla 6 template)
// =============================================================================
// Vite compiles this into media/js/template.js (+ media/css/template.css). It:
//   1. pulls in the compiled styles (SCSS design system + Tailwind utilities),
//   2. mounts Vue3 "islands" — small interactive components mounted onto plain
//      server-rendered markup (template/module/article HTML) via a
//      data-vue="<name>" attribute.
//
// ISLANDS PATTERN
// ---------------
// Markup stays server-rendered (template index.php, modules, articles). Where a
// bit of interactivity is needed, add a mount point:
//     <div data-vue="carousel"></div>
// register the component below, and it mounts automatically on load. Props are
// passed as individual data-* attributes, one per prop, using the prop's own
// name in kebab-case:
//     <div data-vue="carousel" data-target="…" data-arrows-target="…"></div>
// `mountIslands()` reads them off `el.dataset`, which the browser already
// camelCases the same way Vue prop names are written (data-arrows-target →
// dataset.arrowsTarget === the `arrowsTarget` prop) — no manual mapping needed.
// There is no JSON-blob mechanism any more (that was `data-props`, retired);
// a stray `data-props` left on a mount point is NOT read as props — it just
// logs a console warning so a missed conversion fails loudly instead of
// silently losing its props. See mountIslands() for the exact harvesting rule
// and its documented limitations (string-only values, what counts as a prop).
//
// ISLAND LIFECYCLE (M2)
// ---------------------
// `mountIslands(root)` / `unmountIslands(root)` are EXPORTED and take a subtree
// (root itself included when it matches). Server-rendered markup can therefore
// be replaced at runtime — the instant-apply filter chunk swaps the whole
// `#aq-results` region, which holds one `carousel` island per line band — with
// the Vue apps torn down before the old nodes go and re-created on the new
// ones. Feature chunks receive the two functions as INJECTED ARGUMENTS
// (`init({ mountIslands, unmountIslands })`); a chunk must never import this
// entry back (see the BOOTED_FLAG tripwire near the bottom).
// =============================================================================

import '../scss/template.scss';
import '../css/tailwind.css';

import {createApp} from 'vue';

// --- Island registry -------------------------------------------------------
// Map data-vue names → component loaders (dynamic import = code-split friendly,
// though the build inlines everything into one IIFE bundle).
const islands = {
    'carousel': () => import('./components/Carousel.vue'),
    // com_aquaestil (site front end). `Lightbox.vue` is deliberately absent: it is
    // a CHILD of Gallery.vue, not an island — two createApp roots cannot talk to
    // each other.
    // Mounted TWICE on a product page from this one entry (the island ceiling
    // counts entries, not mounts): the hero photo grid and — M4 — the technical
    // drawings, which pass `data-slider="false"`.
    'aq-gallery': () => import('./com_aquaestil/Gallery.vue'),
    'aq-section-nav': () => import('./com_aquaestil/SectionNav.vue'),
    // M2: the mobile filter overlay on the products listing. Open/close ONLY —
    // it never reads a filter value (features/aquaestil-filters.js owns that).
    'aq-filter-overlay': () => import('./com_aquaestil/FilterOverlay.vue'),
    // Mounted ONCE per page and shared by every `[data-aq-drawer-open]` trigger
    // (only one drawer can be open at a time, and a second instance would fight
    // the first for the scroll-lock class). Not the prototype's `Drawer.vue`,
    // which is a child of the M4 configurator — see the file's own docblock.
    'aq-drawer': () => import('./com_aquaestil/DrawerIsland.vue'),
    // M4: the Kompatibilnost configurator. ONE mount per configurator group; it
    // only swaps which panel carries the `hidden` attribute. It deliberately owns
    // NO drawer — `aq-drawer` above already serves every `[data-aq-drawer-open]`
    // trigger on the page, panels included (delegated on `document`).
    'aq-configurator': () => import('./com_aquaestil/Configurator.vue'),
    // The colour configurator on a furniture set's detail page. ONE mount per
    // page; like `aq-configurator` it only moves the `hidden` attribute between
    // nodes the server already sent — here the finished colour renders, their
    // panels and the per-element swatch blocks. It needs NO entry in
    // `primeConfiguratorState()` below: that machinery exists because
    // Kompatibilnost is served in its "everything visible" no-JS state, and this
    // section is served in its final default state instead, so there is nothing
    // to hide before first paint and nothing to un-hide if the chunk never loads.
    'aq-konfigurator': () => import('./com_aquaestil/Konfigurator.vue'),
};

// --- First-paint state for `aq-configurator` (review I1) ------------------
// The Kompatibilnost section is SERVED IN ITS NO-JS STATE: every detail panel is
// shipped with no `hidden` attribute and every drawer body is revealed by
// `html:not(.has-aq-configurator)` (contracts §1 / C4 §1.2 deviation 2). That is
// the right thing to send to a reader without JavaScript — and the wrong thing to
// paint for everyone else, because `Configurator.vue` lives in a lazily imported
// chunk and cannot collapse the stack until that chunk resolves. On a cold cache
// the gap is four full panels (an `aspect-ratio: 648/289` image, an `<h3>` and a
// paragraph each) plus four drawer bodies, painted and then thrown away: several
// thousand pixels of layout shift on the most-visited product family.
//
// ⚠️ A PREVIOUS NOTE IN THIS REPO (and in `_item-details.scss`) CLAIMED THAT
//    SETTING `readyClass` EARLY WAS BY ITSELF THE FIX. It is not, and that claim
//    is now corrected everywhere it appeared. It was written against the
//    PROTOTYPE, whose partial shipped the non-active panels WITH `hidden`, so
//    turning the fallback off early was enough. Here nothing is hidden: the panels
//    are painted by `.aq-configurator__panel{display:flex}`, the component's own
//    base rule, which no amount of early class-setting touches.
//
// The fix is BOTH halves, and neither works alone:
//   1. this function, which sets the classes synchronously at entry evaluation —
//      the entry is a `type="module"` script, so this runs after parsing and
//      before `DOMContentLoaded`, i.e. before `boot()`, before the lazy chunk, and
//      IN PRACTICE before first paint. "In practice" is exact wording, not hedging
//      (review M7): browsers paint incrementally DURING parsing, so on a slow or
//      streamed response the panels can still show for a moment before the class
//      lands. This shrinks the window from "an extra network round-trip for the
//      lazy chunk" to "the tail of HTML parsing"; closing it completely would take
//      an inline `<script>` in `<head>`, which this template does not do for
//      anything;
//   2. `_overrides-m4.scss` block 6, which hides `:nth-child(n + 2)` of the panel
//      list. The first panel survives, and it is exactly the one the island
//      activates (`initialKey()` falls back to `optionEls[0]`), so the
//      pre-enhancement paint IS the enhanced paint and there is nothing to shift.
//
// ⚠️ TWO CLASSES, TWO LIFETIMES, AND THE SECOND ONE IS NOT OPTIONAL.
//    `has-aq-configurator` is the island's `readyClass` and is PERMANENT: it turns
//    off the `html:not(…)` no-JS fallback, which is what collapses the drawer
//    bodies and restores the drawn row layout.
//    `is-aq-configurator-pending` is TRANSIENT — `Configurator.vue` removes it as the
//    FIRST statement of `onMounted`, not the last, so that its two selector guards
//    cannot `return` past the removal — and block 6 keys on THAT one.
//    Keying block 6 on the permanent class instead looks equivalent and is not:
//    the selector outranks `.aq-configurator__panel { display: flex }` (0,4,1 vs
//    0,1,0), so it would keep hiding panels 2…N after the island removed their
//    `hidden`, leaving a configurator where only the first option ever works.
//    A class the island retires means that after mount, CSS has no opinion about
//    which panel shows and the `hidden` attribute is the sole mechanism again.
//
// It is deliberately scoped to pages that actually carry a configurator host: the
// class also governs `.aq-drawer__source` visibility, and setting it on a page
// with no configurator would hide the M1 feature/accessory drawer bodies from a
// no-JS reader for no reason. (M1 sources are additionally protected by
// `_overrides-m1.scss` block 12(a), but relying on that would be relying on a rule
// written for a different purpose.)
//
// ⚠️ THE FAILURE MODE THIS BUYS, AND WHY IT IS NOT LEFT STANDING (review I3).
//    Priming is a PROMISE that the island will arrive. When it does not, the promise
//    becomes a lie in three places at once, and the round-1 note that called the cost
//    "only the first system is readable" undercounted it:
//      · panels 2…N are hidden by `_overrides-m4.scss` block 6 — the part that was
//        recorded;
//      · ALL FOUR drawer bodies are hidden too, because `has-aq-configurator` also
//        switches off `html:not(…) .aq-drawer__source { display: flex }` — this is
//        the part that was missed, and it is the bigger half;
//      · the `<a href="#source-id">` recovery route dies with them: DELTA 1 in the
//        layout exists so a reader WITHOUT the island can still reach the body a
//        control discloses, and it jumps to a `display: none` target.
//
//    And the trigger is ordinary. Vite emits this entry under a FIXED name and
//    Joomla serves it as `template.js?<media-version>` — a cachebuster, not a
//    content hash — while the lazy chunks are content-hashed and the deploy
//    procedure purges superseded ones. So any client holding a cached entry across a
//    redeploy that changed `Configurator.vue` imports a URL that is now a 404. That
//    is a routine release, not an exotic network event.
//
//    `unprimeConfiguratorState()` is therefore not a nicety: it is the other half of
//    the promise. On any failure the priming is UNWOUND — both classes, see that
//    function — and the page falls back to exactly the round-0 degradation:
//    everything visible, nothing enhanced. The residual cost is then only what it
//    was always worth: a first-paint reflow avoided on every cold load, paid for by
//    a brief un-enhanced render on the rare failed one.
function primeConfiguratorState() {
    if (!document.querySelector('[data-vue="aq-configurator"]')) {
        return;
    }

    // `has-aq-configurator` is the same literal as `Configurator.vue`'s `readyClass`
    // default and the `data-ready-class` the layout emits; `is-aq-configurator-pending`
    // is shared with that component's `PENDING_CLASS` and with `_overrides-m4.scss`
    // block 6. Both are strings duplicated across files by necessity (an entry cannot
    // import a lazy chunk's constants without pulling the chunk in) — if either ever
    // changes, change every copy.
    document.documentElement.classList.add('has-aq-configurator', 'is-aq-configurator-pending');
}

/**
 * Undo `primeConfiguratorState()`. Called whenever the configurator island cannot
 * be mounted, from both guards below (the per-island `catch` and `boot()`'s).
 *
 * ⚠️ BOTH CLASSES, AND THE SECOND ONE IS THE ONE THAT MATTERS. Removing only
 * `is-aq-configurator-pending` un-hides the panels and looks like a fix — and
 * leaves all four drawer bodies `display: none`, because the PERMANENT
 * `has-aq-configurator` is what switches off the `html:not(…) .aq-drawer__source`
 * fallback. That half is invisible until somebody follows a `Saznaj više` link and
 * lands nowhere. Do not "simplify" this to one class.
 *
 * Idempotent, and safe to call on a page that was never primed.
 *
 * @return  {void}
 */
function unprimeConfiguratorState() {
    document.documentElement.classList.remove('has-aq-configurator', 'is-aq-configurator-pending');
}

if (document.readyState === 'loading') {
    // Defensive only: `joomla.asset.json` loads this file as `type="module"`, which
    // is deferred, so the document is already parsed by the time this runs. Kept so
    // a future non-module load cannot silently turn the priming into a no-op.
    document.addEventListener('DOMContentLoaded', primeConfiguratorState);
} else {
    primeConfiguratorState();
}

// Keep in sync with `$nav-breakpoint` ('xl') in assets/scss/_settings.scss —
// the single point where the desktop pill nav becomes the mobile overlay panel.
const NAV_BREAKPOINT_PX = 1440;
const isDesktopNav = () => window.matchMedia(`(min-width: ${NAV_BREAKPOINT_PX}px)`).matches;

// --- Header: mobile nav overlay (toggle-only) -------------------------------
// There is ONE nav tree in the DOM (`.site-nav`, rendered by the mod_menu
// override). Below `$nav-breakpoint`, `.site-header__pill` — which already
// wraps that nav + the CTA module — becomes the slide-in overlay panel via
// CSS alone (_header.scss). This just toggles `.is-open` / `aria-expanded`;
// it never renders menu markup.
function initMobileNav() {
    const toggle = document.querySelector('.mobile-nav-toggle');
    const panel = document.querySelector('.site-header__pill');
    if (!toggle || !panel) return;
    const root = document.documentElement;
    const setOpen = (open) => {
        panel.classList.toggle('is-open', open);
        toggle.classList.toggle('is-active', open);
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        root.classList.toggle('has-mobile-nav-open', open);
    };
    toggle.addEventListener('click', () => setOpen(!panel.classList.contains('is-open')));
    const closeBtn = panel.querySelector('.site-header__pill-close');
    if (closeBtn) closeBtn.addEventListener('click', () => setOpen(false));
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') setOpen(false);
    });
}

// Disclosure for each `.nav-dropdown` (Proizvodi / O nama / …) inside
// `.site-nav`. ONE mechanism at every width (R-G-1): the `button.nav-link--toggle`
// is the only thing that opens its mega-menu, and `.is-open` is the only signal
// CSS reads. Below `$nav-breakpoint` the menu lays out inline (an accordion);
// from there up it is the absolute dropdown — see `_header.scss`, where the
// `:hover` / `:focus-within` openers were removed to match.
//
// Three closers, all of them the same `setOpen(false)`:
//   · pressing the toggle again;
//   · a click anywhere outside the group that owns the open menu — including on
//     ANOTHER group's toggle, which is why the loop closes its siblings first;
//   · Escape.
// The outside-click listener is bound ONCE on the document, not once per
// dropdown, and it runs on `click` (not `mousedown`) so it cannot race the
// toggle's own handler — by the time it fires, `.is-open` already reflects the
// press and `contains()` decides the rest.
function initNavAccordion() {
    const dropdowns = [...document.querySelectorAll('.site-nav .nav-dropdown')];
    if (!dropdowns.length) return;

    const setOpen = (dropdown, open) => {
        dropdown.classList.toggle('is-open', open);
        const btn = dropdown.querySelector(':scope > .nav-link--toggle');
        if (btn) btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    };
    const closeAll = (except) => dropdowns.forEach((d) => {
        if (d !== except) setOpen(d, false);
    });

    dropdowns.forEach((dropdown, i) => {
        const btn = dropdown.querySelector(':scope > .nav-link--toggle');
        if (!btn) return;
        // Mirrors the old mobile-only UX default (first group open) — only at
        // mobile widths, so desktop buttons don't start mislabelled "expanded".
        setOpen(dropdown, i === 0 && !isDesktopNav());
        btn.addEventListener('click', () => {
            const willOpen = !dropdown.classList.contains('is-open');
            // Only one mega-menu at a time: on desktop two open absolute panels
            // overlap each other, and on mobile two open accordions push the CTA off
            // the bottom of the overlay.
            closeAll(dropdown);
            setOpen(dropdown, willOpen);
        });
    });

    document.addEventListener('click', (e) => {
        const inside = e.target instanceof Element ? e.target.closest('.site-nav .nav-dropdown') : null;
        closeAll(inside);
    });

    document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        // ⚠️ ONE ESCAPE, ONE ACTION. `initMobileNav()` also listens for Escape, and
        // below `$nav-breakpoint` its panel CONTAINS these dropdowns — so a single
        // press used to close the accordion AND the whole overlay, after which this
        // handler called `.focus()` on a toggle inside a panel that was already
        // hidden and focus fell to `<body>`. Inside the open overlay, Escape belongs
        // to the overlay; here it is only the desktop mega-menu's dismissal.
        if (document.querySelector('.site-header__pill.is-open')) return;
        const open = dropdowns.find((d) => d.classList.contains('is-open'));
        if (!open) return;
        setOpen(open, false);
        // Escape must leave focus somewhere sensible — the control that owns the
        // menu it just closed, exactly as the filter overlay and the drawer do.
        open.querySelector(':scope > .nav-link--toggle')?.focus();
    });
}

// Native <details> language switcher — just close it on outside click.
function initLangSwitch() {
    document.addEventListener('click', (e) => {
        document.querySelectorAll('details.lang-switch[open]').forEach((d) => {
            if (!d.contains(e.target)) d.removeAttribute('open');
        });
    });
}

function initHeader() {
    initMobileNav();
    initNavAccordion();
    initLangSwitch();
}

// --- Island lifecycle registry ---------------------------------------------
// M2 made islands re-mountable: the instant-apply filter chunk replaces the
// whole `#aq-results` subtree, which contains one `carousel` island per line
// band. Those Vue apps must be torn down BEFORE their host nodes are discarded
// (otherwise their `resize`/`scroll` listeners keep pointing at detached DOM)
// and re-created on the freshly inserted markup.
//
// The map is a WeakMap so a discarded host node is collectable the moment the
// caller drops it — there is no manual GC list to keep honest. `null` is a
// RESERVATION written before the component's dynamic import() is awaited: it
// makes `mountIslands()` idempotent against concurrent calls, and lets an
// `unmountIslands()` that lands mid-import cancel the pending mount (the mount
// finds its reservation gone and unmounts the app it just created).
const mountedIslands = new WeakMap();

/**
 * Every `[data-vue]` element inside `root`, INCLUDING `root` itself when it is
 * one (the chunk hands us the swapped-in `#aq-results`, whose own node could
 * carry the attribute).
 *
 * @param   {Document|Element}  root
 * @return  {Element[]}
 */
function islandNodes(root) {
    const found = [];
    if (typeof root.matches === 'function' && root.matches('[data-vue]')) {
        found.push(root);
    }
    if (typeof root.querySelectorAll === 'function') {
        found.push(...root.querySelectorAll('[data-vue]'));
    }
    return found;
}

/**
 * Mount every not-yet-mounted island inside `root`. Idempotent: calling it
 * twice over the same subtree mounts nothing the second time.
 *
 * @param   {Document|Element}  root  defaults to the whole document
 * @return  {Promise<void>}
 */
export async function mountIslands(root = document) {
    const nodes = islandNodes(root);
    for (const el of nodes) {
        if (mountedIslands.has(el)) {
            continue;
        }
        const name = el.getAttribute('data-vue');
        const loader = islands[name];
        if (!loader) {
            // eslint-disable-next-line no-console
            console.warn(`[aquaestil] no Vue island registered for "${name}"`);
            continue;
        }
        // Harvest props from data-* attributes. `el.dataset` already excludes
        // non-data-* attributes (id, class, …) and camelCases each key the same
        // way Vue prop names are written, so `vue` (the island name — not a
        // prop) is the only key that needs stripping out on purpose. An
        // attribute that's simply absent has no key in `dataset` at all, so a
        // missing optional prop comes through as `undefined` (Vue's `default`
        // applies) rather than an empty string.
        //
        // Rule for what counts as a prop: EVERY other data-* attribute on the
        // mount element is harvested, unconditionally — there's no allowlist.
        // Don't add unrelated data-* hooks (analytics markers, test ids, …) to a
        // mount <div>; they would be passed through as an unknown prop, which
        // Vue then renders back onto the root element as a fallthrough
        // attribute rather than silently dropping.
        //
        // Values are always strings (that's what dataset gives you). Every
        // current island prop (Carousel.vue: target/indicator/arrowsTarget) is
        // typed String, so this is a non-issue today. A future Number or
        // Boolean prop would need its OWN coercion — either in the component
        // (e.g. `Number(props.count)`) or added here — this function does not
        // attempt any type coercion.
        const {vue, props: legacyProps, ...props} = {...el.dataset};
        if (legacyProps !== undefined) {
            // The JSON data-props mechanism was retired in favour of individual
            // data-* attributes (see file header). This island still has one —
            // it is NOT read as props, so warn loudly rather than let it lose
            // its props silently.
            // eslint-disable-next-line no-console
            console.warn(`[aquaestil] island "${name}" has a leftover data-props attribute — the JSON prop mechanism was retired, convert it to individual data-* attributes`);
        }
        // Reserve BEFORE the await so a second concurrent pass skips this node.
        mountedIslands.set(el, null);

        // ⚠️ ONE ISLAND MAY NOT TAKE THE PAGE DOWN WITH IT (review I3).
        // This loop is sequential and `await loader()` is a network fetch of a
        // content-hashed chunk, so before this `try` a single rejected import aborted
        // the whole pass and every island AFTER it in document order simply never
        // mounted. On a kade product page the host order is
        //   aq-gallery · aq-section-nav · aq-configurator · carousel · carousel ·
        //   aq-gallery · aq-drawer
        // so the configurator failing took both carousels, the drawings gallery AND
        // the drawer island with it — the drawer being the one that still had a job to
        // do. The fragility was pre-existing; what made it urgent is that a failed
        // configurator now also leaves primed CSS hiding content behind it.
        try {
            const mod = await loader();

            // `createApp().mount()` returns the component PROXY, not the app — keep the
            // app itself, it is the only thing that owns `unmount()`.
            const app = createApp(mod.default, props);
            app.mount(el);

            if (!mountedIslands.has(el)) {
                // `unmountIslands()` ran while the chunk was in flight — the host node is
                // on its way out. Tear the app down immediately so the component's own
                // `onBeforeUnmount` cleanup (listeners, scroll locks, `documentElement`
                // classes) still runs exactly once.
                app.unmount();
                continue;
            }

            mountedIslands.set(el, app);
        } catch (error) {
            // Drop the reservation, or this node is poisoned for the rest of the page's
            // life: `mountedIslands.has(el)` would stay true and every later
            // `mountIslands()` — the filter chunk re-runs it after each results swap —
            // would skip it forever.
            mountedIslands.delete(el);

            if (name === 'aq-configurator') {
                // The island promised at first paint is not coming. Hand the page back the
                // state the server sent, or three panels and four drawer bodies stay hidden
                // with nothing left to reveal them.
                unprimeConfiguratorState();
            }

            // eslint-disable-next-line no-console
            console.error(`[aquaestil] island "${name}" failed to mount; continuing with the rest`, error);
        }
    }
}

/**
 * Unmount every island this module mounted inside `root` (root included).
 * Safe to call on a subtree that holds none.
 *
 * @param   {Document|Element}  root  defaults to the whole document
 * @return  {void}
 */
export function unmountIslands(root = document) {
    for (const el of islandNodes(root)) {
        if (!mountedIslands.has(el)) {
            continue;
        }
        const app = mountedIslands.get(el);
        mountedIslands.delete(el);
        if (app) {
            app.unmount();
        }
    }
}

// --- Progressive enhancement: generic Choices + per-view features -----------
// Each is dynamic-import()ed only when its markers exist on the page. With the
// ES-module build output these become their own lazy chunks, so the Choices
// library/CSS and the distributers behaviour never load on pages without them.

// Generic: any `select[data-choices]` anywhere becomes a Choices combobox,
// configured from its data-* attributes (see modules/choices.js).
async function enhanceSelects() {
    if (!document.querySelector('select[data-choices]')) return;
    const {enhanceChoices} = await import('./modules/choices.js');
    enhanceChoices();
}

async function initScrollToTop() {
    document.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-scroll-top]');
        if (!btn) return;
        window.scrollTo({top: 0, behavior: 'smooth'});
    });
}

// com_distributers "prodajna mreža": list collapse/category switch + keeping the
// filter Choices instances in sync with distributers.js's country→city cascade.
// The <select>s themselves are enhanced generically above; this only adds the
// view-specific behaviour, so load order versus that generic pass doesn't matter.
async function initDistributersFilters() {
    if (!document.querySelector('[data-list-switch], [data-list-toggle]')) return;
    const {init} = await import('./features/distributers-filters.js');
    init();
}

// GLightbox: only load it when a trigger already exists in the DOM (mirrors
// enhanceSelects()). Selectors must match modules/lightbox.js's own.
async function initLightbox() {
    if (!document.querySelector('.glightbox, [data-glightbox], .glightbox-gallery')) return;
    const {initLightbox: init} = await import('./modules/lightbox.js');
    init();
}

// PhotoSwipe: second, independent lightbox (modules/photoswipe.js) — only
// loaded when its own `.pswp-gallery` marker exists, so it never collides
// with GLightbox above.
async function initPhotoswipe() {
    if (!document.querySelector('.pswp-gallery')) return;
    const {initPhotoswipe: init} = await import('./modules/photoswipe.js');
    init();
}

// com_aquaestil: print ONE image — a technical drawing, an element card, a
// kompozicija (R-PD-10 / R-PD-19, contract §5). The button carries the ORIGINAL
// image's URL in `data-aq-print-image`, and `features/aquaestil-print.js` prints
// exactly that in a hidden same-origin iframe.
//
// ⚠️ THE OLD WHOLE-PAGE HANDLER IS GONE. `initAqPrint()` used to answer the bare
//    `[data-aq-print]` attribute with `window.print()`, which printed the entire
//    product page to get at one drawing. Both halves changed together: the
//    component now emits `data-aq-print-image` with a URL in it, and this gate
//    reads the new attribute. A page still carrying only the old bare attribute
//    prints nothing — that is the lockstep, and it is why the two ship together.
async function initAqPrint() {
    if (!document.querySelector('[data-aq-print-image]')) return;
    const {init} = await import('./features/aquaestil-print.js');
    init();
}

// com_aquaestil listing hero: collapse an over-long intro behind a
// "Pročitaj više" toggle. The server always prints the FULL text, so this is
// pure enhancement — see features/aquaestil-readmore.js.
async function initAqReadMore() {
    if (!document.querySelector('[data-aq-readmore]')) return;
    const {init} = await import('./features/aquaestil-readmore.js');
    init();
}

// com_aquaestil `Video demonstracija`: swap the poster facade for a
// youtube-nocookie iframe on click. Nothing third-party loads until then.
async function initAqVideo() {
    if (!document.querySelector('[data-aq-video]')) return;
    const {init} = await import('./features/aquaestil-video.js');
    init();
}

// com_aquaestil products listing: instant-apply filtering (M2). Gated on the
// filter form, so a family whose facet registry yields nothing never loads it.
//
// ⚠️ THE CHUNK MUST NEVER `import` THIS ENTRY. It needs `mountIslands` /
// `unmountIslands` to re-run the carousel islands inside the swapped results
// region, and a plain `import '../template.js'` would reintroduce the
// double-evaluation trap the BOOTED_FLAG below exists to catch (Joomla loads
// the entry as `template.js?cachebuster`; a chunk would import the bare
// `./template.js` — two module URLs, two evaluations, every listener twice).
// So the two functions are INJECTED as arguments instead. Keep it that way.
async function initAqFilters() {
    if (!document.querySelector('[data-aq-filters-form]')) return;
    const {init} = await import('./features/aquaestil-filters.js');
    init({mountIslands, unmountIslands});
}

// Guard against this entry being evaluated more than once. It is not paranoia:
// Joomla's asset manager loads it with a cache-busting query (`template.js?ab12cd`)
// while any chunk that imports it back writes the bare `./template.js`. To the
// browser those are two different modules, so both get evaluated and every
// listener binds twice — which silently broke the mobile nav (the toggle opened
// the panel, then the duplicate listener saw `.is-open` and closed it again).
//
// vite.config.js now keeps the entry out of every chunk's import graph, so this
// should never fire. It stays as a tripwire: a future shared module hoisted into
// the entry would otherwise reintroduce the same invisible double-binding.
const BOOTED_FLAG = '__aquaestilBooted';

function boot() {
    if (window[BOOTED_FLAG]) {
        // eslint-disable-next-line no-console
        console.warn('[aquaestil] template.js was evaluated twice — something is importing the entry chunk (check vite.config.js manualChunks); skipping the duplicate boot');
        return;
    }
    window[BOOTED_FLAG] = true;

    initHeader();
    enhanceSelects();
    initDistributersFilters();
    initLightbox();
    initPhotoswipe();
    initAqPrint();
    initAqReadMore();
    initAqVideo();
    initAqFilters();
    initScrollToTop();

    // The per-island `try`/`catch` in `mountIslands()` already contains a failing
    // chunk, so this is the outer net rather than the primary guard: it catches
    // anything that escapes the loop itself (a throw from `islandNodes()`, a
    // `createApp` failure outside the guarded body, a future edit). Without it a
    // rejection here is an unhandled promise — no console entry naming the cause, and
    // the configurator priming left standing over hidden content.
    mountIslands().catch((error) => {
        // eslint-disable-next-line no-console
        console.error('[aquaestil] island mounting failed', error);
        // BOTH classes — see `unprimeConfiguratorState()`. Unconditional here because
        // at this point we no longer know which island brought the pass down.
        unprimeConfiguratorState();
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
} else {
    boot();
}
